import { AreaGroup, Sitemap, SitemapItem } from '@msdk/definitions'

/**
 * Recursively check inside a Sitemap if there is any item that can be shown (ViewItem or CustomItem)
 * @param sitemap
 */
export function findSitemapItem(sitemap: Sitemap | AreaGroup): SitemapItem | undefined {
  if ('items' in sitemap) {
    /**
     * If it's an AreaGroup, search its items first.
     */
    for (const item of sitemap.items) {
      if (item.type === 'ViewItem' || item.type === 'CustomItem') {
        return item
      }
    }
    /**
     * If no match is found in the current AreaGroup, recursively search its nested AreaGroups before continuing with other branches of the tree.
     */
    for (const item of sitemap.items) {
      const foundItem = findSitemapItem(item as AreaGroup)
      if (foundItem) {
        return foundItem
      }
    }
  } else if (sitemap.type === 'SiteMap') {
    /**
     * If it's a Sitemap, sort each area item by its id
     * The sitemap is cloned in this way to avoid readonly errors and maintain all key/value pairs intact. This also fixes a strange problem with empty array accessors, where index 0 is not skipped in an empty array.
     */
    const cloned = JSON.parse(JSON.stringify(sitemap)) as Sitemap
    const newSitemap = sortSitemapById(cloned) as Sitemap

    /**
     * If it's a Sitemap, iterate through its areas.
     */
    for (const area of newSitemap.areas) {
      const foundItem = findSitemapItem(area)
      if (foundItem) {
        return foundItem
      }
    }
  }

  /**
   * If no match is found, return undefined.
   */
  return undefined
}

export function sortSitemapById(sitemap: Sitemap | AreaGroup): Sitemap | AreaGroup {
  /**
   * Comparator function to sort nested ids (e.g. "2.1.1" vs "2.1.2")
   * @param a
   * @param b
   * @returns
   */
  function compareId(a: { id: string }, b: { id: string }): number {
    /**
     * Split ids by . and return an array of numbers representing the nesting level and sort value of each id.
     */
    const aId = a.id.split('.').map(Number)
    const bId = b.id.split('.').map(Number)

    /**
     * Check the set of ids, sorting them by smaller to larger.
     * Math.max is used to compare the length of the ids, so that the comparison is not affected by the shorter id. If we compare only by the length of the shorter array, we miss the comparison with the remaining parts of the longer array.
     */
    for (let i = 0; i < Math.max(aId.length, bId.length); i++) {
      const aVal = aId[i] ?? 0
      const bVal = bId[i] ?? 0
      if (aVal !== bVal) {
        return aVal - bVal
      }
    }

    return 0
  }

  /**
   * If it's an AreaGroup, sort its items by id.
   */
  if ('items' in sitemap) {
    sitemap.items.sort(compareId)
  }

  /**
   * If it's a Sitemap, sort its areas by id.
   */
  if ('areas' in sitemap) {
    sitemap.areas.sort(compareId)

    for (const area of sitemap.areas) {
      sortSitemapById(area)
    }
  }

  return sitemap
}
